import { ChatBubbleLeftEllipsisIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { createContext, useContext, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import AppButton from "../../ui/buttons/AppButton";
import SelectionToolbar from "../../ui/overlays/SelectionToolbar";
import { clearSelection, selectedRangeIn } from "../../shared/selection/selectedRange";
import { referenceWithQuote, sameChatReference, type ChatReference } from "./chatReference";
import { sendToChat, useChatReferences } from "./referenceTray";
import SentToChat from "./SentToChat";

/** The attribute that marks a selection's subject; the innermost one holding a selection takes it. */
const SUBJECT_ATTRIBUTE = "data-chat-subject";

/** The subject a nested one lies in, which its selection sends too (PC-6). */
const EnclosingSubject = createContext<Omit<ChatReference, "quote"> | null>(null);

interface SelectionToChatProps {
  projectId: string;
  /** What a selection inside points at: the selected text becomes its quote. */
  subject: Omit<ChatReference, "quote">;
  /** Offered beside "Ask in chat" when the selected thing can be edited in place. */
  onEdit?: (() => void) | undefined;
  className?: string;
  children: ReactNode;
}

interface Selected {
  readonly text: string;
  readonly rect: DOMRect;
}

/**
 * Select text in what an agent or a person made, and ask about it (F96): a selection inside
 * shows "Ask in chat", which sends `subject` with the selected text to the chat
 * (`referenceTray`), as selecting text in T3 fills its message box; and "Edit" where the
 * thing can be edited in place. Nested subjects narrow it: a span in an annotation set's
 * segment goes as the segment, quoted, and the set goes with it, so the agent reads the labels
 * the person saw (PC-6); a subject with its enclosing one's reference sends it once. Keyboard
 * selections count as mouse ones do.
 */
export default function SelectionToChat({ projectId, subject, onEdit, className, children }: SelectionToChatProps) {
  const { t } = useTranslation();
  const id = useId();
  const enclosing = useContext(EnclosingSubject);
  const container = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<Selected | null>(null);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const read = () => {
      const next = selectionIn(container.current, id);
      setSelected((current) => (next === null ? null : current?.text === next.text ? current : next));
      if (next === null) setSent(false);
    };
    // A selection is read once it is made: after the pointer or key that made it is released.
    const afterRelease = () => { requestAnimationFrame(read); };
    // A selection cleared, or no longer in this view, hides the toolbar at once.
    const onSelectionChange = () => {
      if (selectedRangeIn(container.current) === null) read();
    };
    const hide = () => { setSelected(null); };
    document.addEventListener("pointerup", afterRelease);
    document.addEventListener("keyup", afterRelease);
    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("scroll", hide, true);
    return () => {
      document.removeEventListener("pointerup", afterRelease);
      document.removeEventListener("keyup", afterRelease);
      document.removeEventListener("selectionchange", onSelectionChange);
      window.removeEventListener("scroll", hide, true);
    };
  }, [id]);

  return (
    <div ref={container} {...{ [SUBJECT_ATTRIBUTE]: id }} className={className}>
      <EnclosingSubject.Provider value={subject}>{children}</EnclosingSubject.Provider>
      {selected !== null ? (
        <SelectionToolbar selection={selected.rect} label={t("chatReferences.selection")} onClose={() => { setSelected(null); }}>
          {sent ? (
            <SentToChat projectId={projectId} />
          ) : (
            <AppButton
              variant="ghost"
              size="xs"
              onClick={() => {
                // The thing it lies in goes too, as it is: a span's annotation set carries the labels (PC-6).
                if (enclosing !== null && enclosing.reference !== subject.reference) sendToChat(projectId, { ...enclosing, quote: null });
                sendToChat(projectId, referenceWithQuote(subject, selected.text));
                setSent(true);
              }}
            >
              <ChatBubbleLeftEllipsisIcon className="h-4 w-4" aria-hidden="true" />
              {t("chatReferences.ask")}
            </AppButton>
          )}
          {onEdit !== undefined ? (
            <AppButton
              variant="ghost"
              size="xs"
              onClick={() => {
                clearSelection();
                setSelected(null);
                onEdit();
              }}
            >
              <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
              {t("chatReferences.edit")}
            </AppButton>
          ) : null}
        </SelectionToolbar>
      ) : null}
    </div>
  );
}

/** The selection's text and place, if this container is the innermost subject holding all of it. */
function selectionIn(container: HTMLElement | null, id: string): Selected | null {
  const range = selectedRangeIn(container);
  if (range === null) return null;
  const common = range.commonAncestorContainer;
  const element = common instanceof Element ? common : common.parentElement;
  if (element?.closest(`[${SUBJECT_ATTRIBUTE}]`)?.getAttribute(SUBJECT_ATTRIBUTE) !== id) return null;
  const text = range.toString();
  return text.trim() === "" ? null : { text, rect: range.getBoundingClientRect() };
}

/** Ask about the whole thing, nothing selected in it: a term, an annotation, an artifact. */
export function AskInChatButton({ projectId, subject }: { projectId: string; subject: Omit<ChatReference, "quote"> }) {
  const { t } = useTranslation();
  const whole = { ...subject, quote: null };
  // In the chat while the tray holds it: removed from the composer, or sent, it can be asked about again (Sol D3).
  const inChat = useChatReferences(projectId).some((kept) => sameChatReference(kept, whole));
  if (inChat) return <SentToChat projectId={projectId} />;
  return (
    <AppButton
      variant="ghost"
      size="xs"
      aria-label={t("chatReferences.askAbout", { name: subject.name })}
      onClick={() => {
        sendToChat(projectId, whole);
      }}
    >
      <ChatBubbleLeftEllipsisIcon className="h-4 w-4" aria-hidden="true" />
      {t("chatReferences.ask")}
    </AppButton>
  );
}
